"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowLeftIcon, HomeIcon } from "@heroicons/react/24/outline";

export default function NotFound() {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Top decorative element */}
      <div className="absolute top-0 w-full h-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600"></div>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(to right, rgba(37, 99, 235, 0.03) 1px, transparent 1px), 
                            linear-gradient(to bottom, rgba(37, 99, 235, 0.03) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
        
        <div className="absolute left-0 top-1/3 w-[500px] h-[500px] bg-blue-900/10 rounded-full filter blur-[120px] opacity-50"></div>
        <div className="absolute right-0 bottom-1/4 w-[400px] h-[400px] bg-indigo-900/10 rounded-full filter blur-[100px] opacity-40"></div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row items-center justify-center px-6 py-16 relative z-10">
        {/* Left content - SVG and graphics */}
        <div className="w-full md:w-1/2 flex justify-center mb-10 md:mb-0">
          <div className="relative w-72 h-72 md:w-96 md:h-96">
            <div className="absolute w-full h-full animate-pulse-slow opacity-50 rounded-full bg-blue-500/10"></div>
            <div className="absolute w-full h-full animate-spin-slow">
              <div className="absolute top-0 left-1/2 w-2 h-2 rounded-full bg-blue-400 shadow-lg shadow-blue-400/50"></div>
              <div className="absolute top-1/4 right-0 w-3 h-3 rounded-full bg-indigo-400 shadow-lg shadow-indigo-400/50"></div>
              <div className="absolute bottom-0 left-1/2 w-2 h-2 rounded-full bg-blue-400 shadow-lg shadow-blue-400/50"></div>
              <div className="absolute top-1/4 left-0 w-3 h-3 rounded-full bg-indigo-400 shadow-lg shadow-indigo-400/50"></div>
            </div>
            <Image
              src="/404-illustration.svg"
              alt="Lost traveler illustration"
              fill
              priority
              className="object-contain relative z-10"
            />
          </div>
        </div>

        {/* Right content - Text and buttons */}
        <div className="w-full md:w-1/2 max-w-md space-y-6 text-center md:text-left">
          <div className="space-y-2">
            <div className="inline-block bg-blue-600/10 px-3 py-1 rounded-full">
              <p className="text-blue-400 text-sm font-semibold tracking-wider">
                ERROR 404
              </p>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              Page not found
            </h1>
            <p className="text-gray-300 max-w-sm">
              We&apos;ve searched our entire playbook, but we couldn&apos;t find
              the page you&apos;re looking for.
            </p>
          </div>

          <div className="space-y-3 max-w-sm">
            <Link
              href="/"
              className="group flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-4 py-3 rounded-lg font-medium shadow-lg shadow-blue-900/20 transition-all duration-200 transform hover:-translate-y-0.5"
            >
              <HomeIcon className="w-5 h-5 mr-2" />
              <span>Return to Dashboard</span>
              <span className="ml-2 transform translate-x-0 group-hover:translate-x-1 transition-transform">
                →
              </span>
            </Link>

            <button
              onClick={() => window.history.back()}
              className="flex items-center justify-center w-full border border-gray-700 bg-gray-800/50 hover:bg-gray-800 px-4 py-3 rounded-lg font-medium transition-all duration-200"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              <span>Go Back</span>
            </button>
          </div>

          <div className="pt-4 border-t border-gray-800">
            <p className="text-sm text-gray-400">
              Looking for something specific? Try our{" "}
              <Link
                href="/search"
                className="text-blue-400 hover:text-blue-300 font-medium underline decoration-2 underline-offset-2 decoration-blue-400/30"
              >
                search page
              </Link>{" "}
              or{" "}
              <Link
                href="/contact"
                className="text-blue-400 hover:text-blue-300 font-medium underline decoration-2 underline-offset-2 decoration-blue-400/30"
              >
                contact support
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Add custom animation keyframes for slow pulse and spin */}
      <style jsx global>{`
        @keyframes pulse-slow {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.2;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.3;
          }
        }
        @keyframes spin-slow {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 6s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 30s linear infinite;
        }
      `}</style>
    </div>
  );
}
